(() => {
  if (customElements.get('nebula-purpose')) return;

  const measureTextWidth = (el) => {
    if (!el.firstChild) return 0;
    const range = document.createRange();
    range.selectNodeContents(el);
    return range.getBoundingClientRect().width;
  };

  const SLOT_EXTRA   = 34;
  const SNAP_THRESH  = 0.38;
  const SNAP_LOCK_MS = 900;
  const FADE         = '0.65s ease-in-out';

  class NebulaPurpose extends HTMLElement {
    #track          = null;
    #mobileList     = null;
    #N              = 0;
    #titleStack     = null;
    #titles         = [];
    #medias         = [];
    #texts          = [];
    #logos          = [];
    #mobileItems    = [];
    #slots          = [];
    #titleWidths    = [];
    #slotTextWidths = [];
    #active         = 0;
    #snapping       = false;
    #snapTimer      = null;
    #rafId          = null;
    #controller     = null;

    connectedCallback() {
      this.#track      = this.querySelector('.np__track');
      this.#mobileList = this.querySelector('.np__mobile-list');
      if (!this.#track) return;

      this.#N = parseInt(this.#track.dataset.blocks, 10) || 0;
      if (this.#N < 1) return;

      this.#medias      = this.#bySlide('.np__media-stack .np__media-item');
      this.#mobileItems = this.#bySlide('.np__mobile-sticky .np__mobile-item');

      if (this.#N < 2) return;

      this.#titleStack    = this.querySelector('.np__title-stack');
      this.#titles        = this.#bySlide('.np__title-stack .np__title');
      this.#texts         = this.#bySlide('.np__col--right .np__text-item');
      this.#logos         = this.#bySlide('.np__col--right .np__logo-item');

      this.#slots = [...this.querySelectorAll('.np__tag-slot')].map((slot) => {
        const texts = [...slot.querySelectorAll('.np__tag-text')];
        return { el: slot, texts, hasContent: texts.map((t) => t.dataset.hasContent === '1') };
      });

      this.#titleWidths    = this.#titles.map(measureTextWidth);
      this.#slotTextWidths = this.#slots.map(({ texts }) =>
        texts.map((t) => (t.textContent.trim() ? measureTextWidth(t) : 0) + SLOT_EXTRA)
      );

      const initIndex = this.#indexFromScroll();
      this.#apply(initIndex, false);
      this.#updateStuck();

      this.#controller = new AbortController();
      window.addEventListener('scroll', this.#onScroll, {
        passive: true,
        signal: this.#controller.signal,
      });
    }

    disconnectedCallback() {
      this.#controller?.abort();
      if (this.#rafId) cancelAnimationFrame(this.#rafId);
      clearTimeout(this.#snapTimer);
    }

    #bySlide(sel) {
      return [...this.querySelectorAll(sel)].sort((a, b) => +a.dataset.slide - +b.dataset.slide);
    }

    #activeTrack() {
      if (this.#track?.offsetParent !== null) return this.#track;
      if (this.#mobileList?.offsetParent !== null) return this.#mobileList;
      return null;
    }

    #indexFromScroll() {
      const track = this.#activeTrack();
      if (!track) return 0;
      const scrolled = -track.getBoundingClientRect().top;
      const vh = window.innerHeight;
      return Math.max(0, Math.min(this.#N - 1, Math.round(scrolled / vh)));
    }

    #onScroll = () => {
      if (this.#snapping || this.#rafId) return;
      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = null;
        this.#tick();
      });
    };

    #updateStuck() {
      if (!this.#mobileList || this.#mobileList.offsetParent === null) {
        this.classList.remove('np--stuck');
        return;
      }
      const scrolled = -this.#mobileList.getBoundingClientRect().top;
      const vh       = window.innerHeight;
      this.classList.toggle('np--stuck', scrolled >= 0 && scrolled <= (this.#N - 1) * vh + vh * 0.1);
    }

    #tick() {
      const track = this.#activeTrack();
      if (!track) return;

      this.#updateStuck();

      const scrolled = -track.getBoundingClientRect().top;
      const rawPos   = scrolled / window.innerHeight;

      let target = this.#active;
      if (rawPos > this.#active + SNAP_THRESH && this.#active < this.#N - 1) target = this.#active + 1;
      else if (rawPos < this.#active - SNAP_THRESH && this.#active > 0)       target = this.#active - 1;

      if (target !== this.#active) this.#snap(target);
    }

    #snap(index) {
      this.#snapping = true;
      this.#apply(index, true);

      const track = this.#activeTrack();
      if (track) {
        const trackTop = track.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: trackTop + index * window.innerHeight, behavior: 'smooth' });
      }

      clearTimeout(this.#snapTimer);
      this.#snapTimer = setTimeout(() => { this.#snapping = false; }, SNAP_LOCK_MS);
    }

    #apply(index, animate) {
      this.#active = index;

      for (const arr of [this.#medias, this.#texts, this.#logos]) {
        arr.forEach((el, i) => (i === index ? this.#fadeIn(el, animate) : this.#fadeOut(el, animate)));
      }

      this.#mobileItems.forEach((el, i) => (i === index ? this.#fadeIn(el, animate) : this.#fadeOut(el, animate)));

      this.#titles.forEach((el, i) => {
        el.style.transition = animate ? `opacity ${FADE}` : 'none';
        el.style.opacity    = i === index ? '1' : '0';
      });

      if (this.#titleStack) {
        const isMobile = window.innerWidth < 750;
        this.#titleStack.style.transition = (!isMobile && animate) ? `width ${FADE}` : 'none';
        this.#titleStack.style.width      = isMobile ? '' : `${this.#titleWidths[index]}px`;
      }

      const isMobile = window.innerWidth < 750;
      this.#slots.forEach(({ el, texts, hasContent }, si) => {
        const show = hasContent[index];

        texts.forEach((t, i) => {
          t.style.transition = animate ? `opacity ${FADE}` : 'none';
          t.style.opacity    = (i === index && hasContent[i]) ? '1' : '0';
        });

        if (show) {
          if (el.style.display === 'none') { el.style.display = ''; void el.offsetHeight; }
          el.style.transition = animate ? `opacity ${FADE}` : 'none';
          el.style.opacity    = '1';
          if (!isMobile) el.style.width = `${this.#slotTextWidths[si][index]}px`;
        } else {
          el.style.transition = animate ? `opacity ${FADE}` : 'none';
          el.style.opacity    = '0';
          if (animate) {
            el.addEventListener('transitionend', () => {
              if (parseFloat(el.style.opacity) < 0.01) el.style.display = 'none';
            }, { once: true });
          } else {
            el.style.display = 'none';
            el.style.width   = '';
          }
        }
      });
    }

    #fadeIn(el, animate) {
      if (el.style.display === 'none') {
        el.style.transition = 'none';
        el.style.display    = '';
        void el.offsetHeight;
        const v = el.querySelector('video');
        if (v && v.offsetParent !== null) {
          let n = 0;
          const tryPlay = () => {
            v.play().catch(() => {});
            if (v.paused && ++n < 5) setTimeout(tryPlay, 300);
          };
          tryPlay();
        }
      }
      el.style.transition    = animate ? `opacity ${FADE}` : 'none';
      el.style.opacity       = '1';
      el.style.pointerEvents = '';
    }

    #fadeOut(el, animate) {
      el.style.transition    = animate ? `opacity ${FADE}` : 'none';
      el.style.opacity       = '0';
      el.style.pointerEvents = 'none';
      if (animate) {
        el.addEventListener('transitionend', () => {
          if (parseFloat(el.style.opacity) < 0.01) el.style.display = 'none';
        }, { once: true });
      } else {
        el.style.display = 'none';
      }
    }
  }

  customElements.define('nebula-purpose', NebulaPurpose);
})();
